import React, { useState, useEffect } from 'react';
import { Loader2, CheckCircle2, AlertCircle, Calendar, ShieldAlert, PieChart, Landmark, TrendingUp, History, Info } from 'lucide-react';
import API_BASE_URL from '../../Config';
import { useAuth } from '../../contexts/AuthContext';
import { useLayout } from '../../contexts/LayoutContext';

export default function SocioObligaciones({ obligaciones, loading, formatCurrency, formatDate, socioId }) {
    const { token } = useAuth();
    const { isCompact } = useLayout();
    const [compromisos, setCompromisos] = useState([]);
    const [loadingCompromisos, setLoadingCompromisos] = useState(false);
    
    const [capitalSummary, setCapitalSummary] = useState([]);
    const [capitalMovements, setCapitalMovements] = useState([]);
    const [loadingCapital, setLoadingCapital] = useState(false);

    // Fetch Data
    useEffect(() => {
        if (socioId) {
            fetchCompromisos();
            fetchCapitalData();
        }
    }, [socioId]);

    const fetchCompromisos = async () => {
        setLoadingCompromisos(true);
        try {
            const response = await fetch(`${API_BASE_URL}/compromisos/socio/${socioId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setCompromisos(data);
            }
        } catch (error) {
            console.error("Error fetching compromisos:", error);
        } finally {
            setLoadingCompromisos(false);
        }
    };

    const fetchCapitalData = async () => {
        setLoadingCapital(true);
        try {
            const [resSummary, resMovs] = await Promise.all([
                fetch(`${API_BASE_URL}/socio-capital/${socioId}`, { headers: { 'Authorization': `Bearer ${token}` } }),
                fetch(`${API_BASE_URL}/socio-capital/${socioId}/movimientos`, { headers: { 'Authorization': `Bearer ${token}` } })
            ]);

            if (resSummary.ok) setCapitalSummary(await resSummary.json());
            if (resMovs.ok) setCapitalMovements(await resMovs.json());
        } catch (error) {
            console.error("Error fetching capital data:", error);
        } finally {
            setLoadingCapital(false);
        }
    };

    // Check for alerts
    const today = new Date().toISOString().split('T')[0];
    const activeCommitment = compromisos.find(c =>
        c.estadoCompromiso === 1 // Pendiente
    );

    const isCommitmentForToday = activeCommitment && activeCommitment.fechaPromesa === today;

    // 1. Filtro estricto: Solo Aporte y Solidaridad
    const filteredObligaciones = obligaciones.filter(obl => {
        const concepto = (obl.concepto || '').toLowerCase();
        const tipo = (obl.tipo || '').toLowerCase();
        return concepto.includes('aporte') ||
            concepto.includes('solidaridad') ||
            concepto.includes('gasto') ||
            tipo.includes('aporte') ||
            tipo.includes('solidaridad') ||
            tipo.includes('gasto');
    });

    const isPending = (status) => {
        if (!status) return true;
        const s = status.toUpperCase();
        return s === 'PENDIENTE' || s.includes('PENDIENTE') || s.includes('IMPAGO');
    };

    const isPaid = (status) => {
        if (!status) return false;
        const s = status.toUpperCase();
        return s === 'PAGADO' || s.includes('PAGADO') || s.includes('CANCELADO');
    };

    const pendientes = filteredObligaciones.filter(o => isPending(o.estado));
    const pagadas = filteredObligaciones.filter(o => isPaid(o.estado));

    const ObligationTable = ({ data, title, colorClass }) => {
        if (data.length === 0) return null;
        
        return (
            <div className="space-y-6">
                <div className="flex items-center gap-4">
                    <div className={`w-1.5 h-8 ${colorClass} rounded-full`} />
                    <div>
                        <h2 className="text-xl font-black text-slate-800 tracking-tighter">{title}</h2>
                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Aportes, Solidaridad y otros pasivos mensuales</p>
                    </div>
                </div>

                <div className="enterprise-card border-none overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-slate-50 border-b border-slate-100">
                                <tr>
                                    <th className="px-6 py-4 text-left text-[10px] font-black uppercase tracking-widest text-slate-400">Concepto / Referencia</th>
                                    <th className="px-6 py-4 text-left text-[10px] font-black uppercase tracking-widest text-slate-400">Vencimiento</th>
                                    <th className="px-6 py-4 text-right text-[10px] font-black uppercase tracking-widest text-slate-400">Monto Original</th>
                                    <th className="px-6 py-4 text-right text-[10px] font-black uppercase tracking-widest text-slate-400">Saldo Deuda</th>
                                    <th className="px-6 py-4 text-center text-[10px] font-black uppercase tracking-widest text-slate-400">Estado</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {data.map((obl, index) => {
                                    const paid = isPaid(obl.estado);
                                    return (
                                        <tr key={index} className="hover:bg-slate-50/50 transition-colors group">
                                            <td className="px-6 py-6">
                                                <div className="flex items-center gap-4">
                                                    <div className={`bg-slate-50 p-3 rounded-2xl text-slate-300 transition-all border border-slate-100 ${paid ? 'group-hover:bg-emerald-50 group-hover:text-emerald-500' : 'group-hover:bg-rose-50 group-hover:text-rose-500'}`}>
                                                        {paid ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                                                    </div>
                                                    <div>
                                                        <span className="font-black text-slate-900 block tracking-tight">{obl.concepto}</span>
                                                        <span className="text-[10px] text-slate-400 font-black uppercase tracking-widest mt-1">Ref: {obl.referencia || 'N/A'}</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-6">
                                                <span className="font-bold text-slate-600">
                                                    {formatDate(obl.fechaVencimiento || obl.vencimiento)}
                                                </span>
                                            </td>
                                            <td className="px-6 py-6 text-right font-bold text-slate-400">
                                                {formatCurrency(obl.montoOriginal)}
                                            </td>
                                            <td className="px-6 py-6 text-right">
                                                <span className={`text-lg font-black tracking-tighter ${paid ? 'text-emerald-600' : 'text-rose-600'}`}>
                                                    {formatCurrency(obl.saldo || obl.monto)}
                                                </span>
                                            </td>
                                            <td className="px-6 py-6 text-center">
                                                <span className={`text-[9px] font-black uppercase tracking-widest px-3 py-1 rounded-full border ${
                                                    paid ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-rose-50 text-rose-600 border-rose-100'
                                                }`}>
                                                    {obl.estado?.toUpperCase() || 'PENDIENTE'}
                                                </span>
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="animate-premium-in space-y-12">

            {/* SECCIÓN 1: COMPROMISOS Y ALERTAS */}
            {activeCommitment && (
                <div className={`rounded-[2rem] p-8 border-l-[6px] shadow-2xl shadow-slate-200/50 flex items-start gap-6 animate-premium-in
                    ${isCommitmentForToday ? 'bg-rose-50 border-rose-500' : 'bg-amber-50 border-amber-500'}
                `}>
                    <div className={`p-4 rounded-2xl ${isCommitmentForToday ? 'bg-rose-100 text-rose-600' : 'bg-amber-100 text-amber-600'}`}>
                        {isCommitmentForToday ? <ShieldAlert className="w-8 h-8" /> : <Calendar className="w-8 h-8" />}
                    </div>
                    <div>
                        <h3 className={`text-[11px] font-black uppercase tracking-[0.2em] mb-2 ${isCommitmentForToday ? 'text-rose-800' : 'text-amber-800'}`}>
                            {isCommitmentForToday ? 'Urgente: Compromiso para Hoy' : 'Compromiso de Pago Vigente'}
                        </h3>
                        <p className={`text-xl font-black tracking-tighter ${isCommitmentForToday ? 'text-rose-900' : 'text-amber-900'}`}>
                            Promesa de <span className="text-uni-teal">{formatCurrency(activeCommitment.montoPrometido)}</span> pactada para el <span className="text-uni-teal">{formatDate(activeCommitment.fechaPromesa)}</span>.
                        </p>
                        {activeCommitment.observacion && (
                            <p className="text-xs text-slate-500 mt-3 font-bold italic opacity-70">"{activeCommitment.observacion}"</p>
                        )}
                    </div>
                </div>
            )}

            {/* SECCIÓN 2: RESUMEN DE CAPITAL ACUMULADO (APORTE / SOLIDARIDAD) */}
            <div className="space-y-6">
                <div className="flex items-center gap-4">
                    <div className="w-1.5 h-8 bg-uni-teal rounded-full" />
                    <div>
                        <h2 className="text-xl font-black text-slate-800 tracking-tighter uppercase">Posición de Capital Social</h2>
                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Fondos acumulativos por aportes y solidaridad</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {loadingCapital ? (
                         <div className="col-span-full h-32 flex items-center justify-center bg-white rounded-3xl border border-slate-100">
                             <Loader2 className="w-8 h-8 animate-spin text-uni-teal" />
                         </div>
                    ) : capitalSummary.length === 0 ? (
                        <div className="col-span-full bg-slate-50 p-8 rounded-3xl border-2 border-dashed border-slate-200 text-center">
                            <Info className="w-8 h-8 text-slate-300 mx-auto mb-3" />
                            <p className="text-xs font-black text-slate-400 uppercase tracking-widest">No se han generado registros de capital todavía</p>
                        </div>
                    ) : capitalSummary.map(cap => (
                        <div key={cap.id} className="enterprise-card p-8 bg-white border border-slate-100 group hover:border-uni-teal transition-all">
                            <div className="flex justify-between items-start mb-6">
                                <div className="p-4 bg-slate-50 rounded-2xl text-slate-400 group-hover:bg-uni-teal group-hover:text-white transition-all shadow-sm">
                                    <TrendingUp className="w-6 h-6" />
                                </div>
                                <div className="text-right">
                                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-1">Cuotas Pagadas</span>
                                    <span className="text-lg font-black text-slate-900">{cap.cuotasPagadas}</span>
                                </div>
                            </div>
                            <div>
                                <h3 className="text-[10px] font-black text-uni-teal uppercase tracking-[0.2em] mb-2">{cap.tipoCapital}</h3>
                                <p className="text-3xl font-black text-slate-900 tracking-tighter">{formatCurrency(cap.saldoAcumulado)}</p>
                                <div className="mt-4 pt-4 border-t border-slate-50 flex justify-between items-center">
                                    <span className="text-[8px] font-black text-slate-300 uppercase tracking-widest">Saldo Acumulado</span>
                                    <span className="text-[8px] font-bold text-slate-400 italic">Act: {formatDate(cap.ultimaActualizacion)}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* SECCIÓN 3: OBLIGACIONES PENDIENTES */}
            {loading ? (
                <div className="flex flex-col items-center justify-center py-24 text-slate-400">
                    <Loader2 className="w-10 h-10 animate-spin text-uni-teal mb-6" />
                    <p className="text-[11px] font-black uppercase tracking-[0.3em] animate-pulse">Sincronizando cuentas...</p>
                </div>
            ) : (
                <>
                    <ObligationTable 
                        data={pendientes} 
                        title="Obligaciones por Cobrar" 
                        colorClass="bg-rose-500" 
                    />

                    {/* SECCIÓN 4: DETALLE DE MOVIMIENTOS DE CAPITAL */}
                    {!loadingCapital && capitalMovements.length > 0 && (
                        <div className="space-y-6">
                            <div className="flex items-center gap-4">
                                <div className="w-1.5 h-8 bg-slate-900 rounded-full" />
                                <div>
                                    <h2 className="text-xl font-black text-slate-800 tracking-tighter uppercase">Historial de Movimientos</h2>
                                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Detalle de pagos y acreditaciones al capital</p>
                                </div>
                            </div>

                            <div className="enterprise-card border-none overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-slate-800 text-white">
                                            <tr>
                                                <th className="px-6 py-4 text-left text-[9px] font-black uppercase tracking-widest">Fecha Movimiento</th>
                                                <th className="px-6 py-4 text-left text-[9px] font-black uppercase tracking-widest">Tipo de Capital</th>
                                                <th className="px-6 py-4 text-left text-[9px] font-black uppercase tracking-widest">Concepto / Referencia</th>
                                                <th className="px-6 py-4 text-center text-[9px] font-black uppercase tracking-widest">Operación</th>
                                                <th className="px-6 py-4 text-right text-[9px] font-black uppercase tracking-widest">Monto Acreditado</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {capitalMovements.map(mov => (
                                                <tr key={mov.id} className="hover:bg-slate-50 transition-colors group">
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400 group-hover:bg-slate-900 group-hover:text-white transition-all">
                                                                <Calendar className="w-4 h-4" />
                                                            </div>
                                                            <span className="font-bold text-slate-700 text-xs">{formatDate(mov.fecha)}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <span className="text-[10px] font-black text-uni-teal uppercase tracking-widest">{mov.tipoCapital}</span>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <span className="font-black text-slate-800 block text-xs tracking-tight">{mov.concepto}</span>
                                                        <span className="text-[9px] text-slate-400 font-bold uppercase tracking-widest">Transacción Validada</span>
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <span className={`text-[8px] font-black px-3 py-1 rounded-full uppercase tracking-widest border ${
                                                            mov.tipoMovimiento === 'CREDITO' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-rose-50 text-rose-600 border-rose-100'
                                                        }`}>
                                                            {mov.tipoMovimiento}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 text-right">
                                                        <span className="text-sm font-black text-slate-900 tracking-tight">
                                                            {formatCurrency(mov.monto)}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {pendientes.length === 0 && capitalMovements.length === 0 && !activeCommitment && (
                        <div className="text-center py-24 bg-white rounded-2xl border-2 border-dashed border-slate-100">
                             <PieChart className="w-12 h-12 text-slate-200 mx-auto mb-4" />
                             <p className="text-sm font-black text-slate-300 uppercase tracking-widest">Sin movimientos registrados</p>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
